import { createListenerMiddleware, isAnyOf } from "@reduxjs/toolkit";
import { isNotNullish, isNumber } from "core/utils";

import {
    hideNotification,
    showErrorNotification,
    showInfoNotification,
    showNotification,
    showSuccessNotification,
    showWarningNotification,
} from "../../../../../components/NotificationAlerts";
import type { NotificationsState } from "../../../../../components/NotificationAlerts/types";

export const DEFAULT_AUTO_CLOSE_MS = 5000;

const showNotificationMatcher = isAnyOf(
    showNotification,
    showSuccessNotification,
    showWarningNotification,
    showInfoNotification,
    showErrorNotification,
);

export const notificationListener = createListenerMiddleware();

// Add the listener
notificationListener.startListening({
    matcher: showNotificationMatcher,
    effect: async (action: { payload: { autoCloseSeconds?: number; autoCloseDisabled?: boolean } }, listenerApi) => {
        const { autoCloseSeconds, autoCloseDisabled } = action.payload;

        // check if autoCloseDisabled is set to true
        if (autoCloseDisabled === true) {
            return;
        }

        // Check if autoCloseSeconds is a positive number
        if (isNumber(autoCloseSeconds) && autoCloseSeconds <= 0) {
            throw new Error("Auto close seconds must be positive number.");
        }
        const closeDelay = isNotNullish(autoCloseSeconds) ? autoCloseSeconds * 1000 : DEFAULT_AUTO_CLOSE_MS;

        // Wait for 3 seconds
        await listenerApi.delay(closeDelay);

        // Check if the notification is still shown
        const state = listenerApi.getState() as { notifications: NotificationsState };
        const notificationState = state.notifications;
        if (!notificationState.show) {
            //     Notification already hidden, skipping auto-close
            return;
        }

        // Then dispatch hideNotification
        listenerApi.dispatch(hideNotification());
    },
});

// Listener for manual dismiss → cancel auto-close immediately
notificationListener.startListening({
    actionCreator: hideNotification,
    effect: async (_action, listenerApi) => {
        listenerApi.cancelActiveListeners();
        // No need to dispatch anything here; manualDismiss already hides the notification
    },
});
